import { Component, ElementRef, QueryList, ViewChild, ViewChildren, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { OuiSelect, OuiSelectOption } from 'omnium-ui/form-field';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiInfosBulle } from 'omnium-ui/infos-bulle';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import {
  BatchMetadataItem,
  CorrectionExecution,
  CorrectionPreview,
  MetadataCorrectionWorkspace,
  ProblematicMetadataRow,
  SuggestedInvestorDeletion,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { buildReplacementOptionChoices, buildReplacementSelections } from '../../shared/correction-preview.utils';
import {
  buildCorrectionExecutionRequest,
  canExecuteCorrection,
  getPreviewableCandidateInvestorId,
  isCorrectionBlocked,
  shouldSkipCorrectionPreview,
} from '../../shared/correction-session.utils';
import { InvestorDeletionScoreDetailsComponent } from '../../shared/investor-deletion-score-details/investor-deletion-score-details.component';
import { InvestorPersonMappingConflictsTableComponent } from '../../shared/investor-person-mapping-conflicts-table/investor-person-mapping-conflicts-table.component';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { buildBatchItems } from '../../shared/problematic-metadata.utils';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';

interface BatchMetadataState {
  excludedInvestorIds: number[];
  completed: boolean;
  execution?: CorrectionExecution;
}

@Component({
  selector: 'dbw-batch-workspace',
  imports: [
    MatTableModule,
    OuiButtonModule,
    OuiInfosBulle,
    OuiSelect,
    OuiTableModule,
    OuiTagModule,
    InvestorCardComponent,
    InvestorDeletionScoreDetailsComponent,
    InvestorPersonMappingConflictsTableComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
    SummaryGridComponent,
  ],
  templateUrl: './batch-workspace.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./batch-workspace.component.scss'],
})
export class BatchWorkspaceComponent {
  readonly appRoutePaths = appRoutePaths;

  @ViewChild('queueContainer') queueContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren('queueCardRef') queueCards?: QueryList<ElementRef<HTMLButtonElement>>;

  readonly loading = signal(true);
  readonly workspaceLoading = signal(false);
  readonly previewLoading = signal(false);
  readonly executing = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly workspaceError = signal<string | undefined>(undefined);
  readonly previewError = signal<string | undefined>(undefined);
  readonly items = signal<BatchMetadataItem[]>([]);
  readonly currentIndex = signal(0);
  readonly workspace = signal<MetadataCorrectionWorkspace | undefined>(undefined);
  readonly preview = signal<CorrectionPreview | undefined>(undefined);
  readonly selectedInvestorId = signal<number | undefined>(undefined);
  readonly previewInvestorId = signal<number | undefined>(undefined);
  readonly replacementSelections = signal<Record<number, number | null>>({});
  readonly replacementOptionChoices = signal<Partial<Record<number, OuiSelectOption<number>[]>>>({});

  private readonly metadataStates = signal(new Map<number, BatchMetadataState>());

  readonly hasItems = computed(() => this.items().length > 0);

  readonly currentItem = computed(() => this.items()[this.currentIndex()]);

  readonly currentState = computed(() => {
    const item = this.currentItem();

    if (!item) {
      return undefined;
    }

    return this.getMetadataState(item.metadataId);
  });

  readonly currentExecution = computed(() => this.currentState()?.execution);

  readonly completedCount = computed(() => this.items().filter(item => this.getMetadataState(item.metadataId).completed).length);

  readonly firstGroupCandidates = computed<SuggestedInvestorDeletion[]>(
    () => this.workspace()?.duplicateAnalysis.firstDuplicateGroup?.rankedCandidates ?? []
  );

  readonly correctionBlocked = computed(() => isCorrectionBlocked(this.workspace()));
  readonly canExecuteMetadataCorrectionAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.problematicMetadataCorrectionExecute)
  );

  readonly canExecute = computed(() => {
    return (
      this.canExecuteMetadataCorrectionAction() &&
      canExecuteCorrection(this.preview(), this.executing(), this.correctionBlocked(), this.replacementSelections())
    );
  });

  readonly canMoveToNext = computed(() => this.findNextPendingIndex() !== -1);

  readonly isCurrentCompleted = computed(() => this.currentState()?.completed ?? false);
  readonly summaryItems = computed<SummaryGridItem[]>(() => {
    const item = this.currentItem();

    if (!item) {
      return [];
    }

    return [
      {
        key: 'progress',
        label: 'Avancement',
        value: `${this.currentIndex() + 1} / ${this.items().length}`,
        meta: this.completedCount() + ' metadata(s) déjà soldée(s).',
      },
      {
        key: 'current-metadata',
        label: 'Metadata courante',
        value: item.metadataId,
        meta: item.investorCount + ' investisseur(s) distinct(s).',
        aside: {
          label: this.getQueueItemLabel(item.metadataId, this.currentIndex()),
          status: this.getQueueItemStatus(item.metadataId, this.currentIndex()),
        },
      },
      {
        key: 'current-avancements',
        label: 'Avancements',
        value: item.avancementCount,
        meta: 'Volume de lignes pour cette metadata.',
      },
      {
        key: 'consultants',
        label: 'Consultants',
        value: item.consultantNames.join(', ') || 'Non renseignés',
        compactValue: true,
        meta: 'Contexte de travail de la metadata sélectionnée.',
      },
    ];
  });

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly router: Router,
    private readonly snackbarService: OuiSnackbarService
  ) {
    this.loadQueue();
  }

  selectItem(index: number): void {
    if (index < 0 || index >= this.items().length || index === this.currentIndex()) {
      return;
    }

    this.currentIndex.set(index);
    this.loadCurrentMetadata();
    this.scrollCurrentQueueItemToTop();
  }

  moveToPrevious(): void {
    if (this.currentIndex() === 0) {
      return;
    }

    this.currentIndex.update(index => index - 1);
    this.loadCurrentMetadata();
    this.scrollCurrentQueueItemToTop();
  }

  moveToNext(): void {
    const nextIndex = this.findNextPendingIndex();

    if (nextIndex === -1) {
      return;
    }

    this.currentIndex.set(nextIndex);
    this.loadCurrentMetadata();
    this.scrollCurrentQueueItemToTop();
  }

  openCurrentMetadata(): void {
    const item = this.currentItem();

    if (!item) {
      return;
    }

    this.router.navigateByUrl(appRoutePaths.minotaure.metadata(item.metadataId));
  }

  reloadCurrentMetadata(): void {
    this.loadCurrentMetadata();
  }

  selectCandidate(candidate: SuggestedInvestorDeletion): void {
    this.previewDeletion(getPreviewableCandidateInvestorId(candidate));
  }

  previewDeletion(investorId: number): void {
    const item = this.currentItem();

    if (!item || !this.canExecuteMetadataCorrectionAction() || this.correctionBlocked()) {
      return;
    }

    if (shouldSkipCorrectionPreview(investorId, this.previewLoading(), this.previewInvestorId(), this.preview())) {
      return;
    }

    this.selectedInvestorId.set(investorId);
    this.previewInvestorId.set(investorId);
    this.preview.set(undefined);
    this.previewError.set(undefined);
    this.previewLoading.set(true);
    this.replacementSelections.set({});
    this.replacementOptionChoices.set({});

    this.apiService.previewCorrection(item.metadataId, investorId, this.getMetadataState(item.metadataId).excludedInvestorIds).subscribe({
      next: preview => {
        this.preview.set(preview);
        this.replacementSelections.set(buildReplacementSelections(preview));
        this.replacementOptionChoices.set(buildReplacementOptionChoices(preview));
        this.previewLoading.set(false);
      },
      error: error => {
        this.previewError.set(error?.error?.detail ?? "Impossible d'analyser la suppression demandée.");
        this.previewLoading.set(false);
      },
    });
  }

  onReplacementSelected(linkedInvestorId: number, replacementInvestorId: number | null): void {
    this.replacementSelections.update(selections => ({
      ...selections,
      [linkedInvestorId]: replacementInvestorId,
    }));
  }

  executeCorrection(): void {
    const item = this.currentItem();
    const selectedInvestorId = this.selectedInvestorId();
    const preview = this.preview();

    if (!item || !selectedInvestorId || !preview || !this.canExecute()) {
      return;
    }

    this.executing.set(true);
    this.previewError.set(undefined);

    const state = this.getMetadataState(item.metadataId);
    const request = buildCorrectionExecutionRequest(preview, selectedInvestorId, state.excludedInvestorIds, this.replacementSelections());

    this.apiService.executeCorrection(item.metadataId, request).subscribe({
      next: execution => {
        this.updateMetadataState(item.metadataId, currentState => ({
          ...currentState,
          execution,
          excludedInvestorIds: execution.updatedVirtualDeletedInvestorIds,
        }));

        this.executing.set(false);

        if (execution.success) {
          this.snackbarService.open('Correction exécutée avec succès.', 'success');
          this.clearPreviewState();
          this.loadCurrentMetadata();
        } else {
          this.snackbarService.open('La correction a échoué.', 'error');
        }
      },
      error: error => {
        this.executing.set(false);
        const message = error?.error?.detail ?? "L'exécution de la correction a échoué.";
        this.previewError.set(message);
        this.snackbarService.open(message, 'error');
      },
    });
  }

  getQueueItemStatus(metadataId: number, index: number): 'completed' | 'pending' | 'to-correct' {
    if (this.getMetadataState(metadataId).completed) {
      return 'completed';
    }

    return index === this.currentIndex() ? 'pending' : 'to-correct';
  }

  getQueueItemLabel(metadataId: number, index: number): string {
    if (this.getMetadataState(metadataId).completed) {
      return 'Terminée';
    }

    return index === this.currentIndex() ? 'En cours' : 'À traiter';
  }

  formatGedDocumentCount(gedDocumentCount: number | null | undefined): string {
    if (gedDocumentCount === null || gedDocumentCount === undefined) {
      return 'Indisponible';
    }

    return `${gedDocumentCount}`;
  }

  hasGedDocumentNames(investor: ProblematicMetadataRow): boolean {
    return investor.gedDocumentNames.length > 0;
  }

  getGedDocumentNamesTitle(investor: ProblematicMetadataRow): string {
    return `Documents GED investisseur ${investor.investorId}`;
  }

  getBoxPlacementOperationsLabel(hasBoxPlacementOperations: boolean | null | undefined): string {
    if (hasBoxPlacementOperations === null || hasBoxPlacementOperations === undefined) {
      return 'Indisponible';
    }

    return hasBoxPlacementOperations ? 'Oui' : 'Non';
  }

  getBoxPlacementOperationsStatus(hasBoxPlacementOperations: boolean | null | undefined): 'completed' | 'todo' | 'pending' {
    if (hasBoxPlacementOperations === null || hasBoxPlacementOperations === undefined) {
      return 'pending';
    }

    return hasBoxPlacementOperations ? 'todo' : 'completed';
  }

  getImmoOptionIntentionLabel(hasImmoOptionIntention: boolean | null | undefined): string {
    if (hasImmoOptionIntention === null || hasImmoOptionIntention === undefined) {
      return 'Indisponible';
    }

    return hasImmoOptionIntention ? 'Oui' : 'Non';
  }

  getImmoOptionIntentionStatus(hasImmoOptionIntention: boolean | null | undefined): 'completed' | 'todo' | 'pending' {
    if (hasImmoOptionIntention === null || hasImmoOptionIntention === undefined) {
      return 'pending';
    }

    return hasImmoOptionIntention ? 'todo' : 'completed';
  }

  private loadQueue(): void {
    this.loading.set(true);
    this.error.set(undefined);

    this.apiService.getProblematicMetadataReport().subscribe({
      next: report => {
        const items = buildBatchItems(report.corrections);

        this.items.set(items);
        this.synchronizeMetadataStates(items);
        this.loading.set(false);

        if (items.length > 0) {
          this.currentIndex.set(Math.min(this.currentIndex(), items.length - 1));
          this.loadCurrentMetadata();
        }
      },
      error: error => {
        this.error.set(error?.error?.detail ?? 'Impossible de charger la file batch.');
        this.loading.set(false);
      },
    });
  }

  private loadCurrentMetadata(): void {
    const item = this.currentItem();

    if (!item) {
      return;
    }

    this.workspaceLoading.set(true);
    this.workspaceError.set(undefined);
    this.workspace.set(undefined);
    this.clearPreviewState();

    this.apiService.getMetadataWorkspace(item.metadataId, this.getMetadataState(item.metadataId).excludedInvestorIds).subscribe({
      next: workspace => {
        this.workspace.set(workspace);
        this.workspaceLoading.set(false);
        this.updateMetadataState(item.metadataId, currentState => ({
          ...currentState,
          completed: !workspace.hasDuplicates,
        }));
      },
      error: error => {
        this.workspaceError.set(error?.error?.detail ?? 'Impossible de charger la metadata.');
        this.workspaceLoading.set(false);
      },
    });
  }

  private clearPreviewState(): void {
    this.preview.set(undefined);
    this.previewError.set(undefined);
    this.previewLoading.set(false);
    this.selectedInvestorId.set(undefined);
    this.previewInvestorId.set(undefined);
    this.replacementSelections.set({});
    this.replacementOptionChoices.set({});
  }

  private synchronizeMetadataStates(items: BatchMetadataItem[]): void {
    const currentStates = this.metadataStates();
    const nextStates = new Map<number, BatchMetadataState>();

    items.forEach(item => {
      nextStates.set(
        item.metadataId,
        currentStates.get(item.metadataId) ?? {
          excludedInvestorIds: [],
          completed: false,
        }
      );
    });

    this.metadataStates.set(nextStates);
  }

  private getMetadataState(metadataId: number): BatchMetadataState {
    return (
      this.metadataStates().get(metadataId) ?? {
        excludedInvestorIds: [],
        completed: false,
      }
    );
  }

  private updateMetadataState(metadataId: number, updater: (state: BatchMetadataState) => BatchMetadataState): void {
    this.metadataStates.update(currentStates => {
      const nextStates = new Map(currentStates);
      const currentState = currentStates.get(metadataId) ?? {
        excludedInvestorIds: [],
        completed: false,
      };

      nextStates.set(metadataId, updater(currentState));
      return nextStates;
    });
  }

  private findNextPendingIndex(): number {
    const items = this.items();
    const currentIndex = this.currentIndex();

    for (let index = currentIndex + 1; index < items.length; index++) {
      if (!this.getMetadataState(items[index].metadataId).completed) {
        return index;
      }
    }

    for (let index = 0; index < currentIndex; index++) {
      if (!this.getMetadataState(items[index].metadataId).completed) {
        return index;
      }
    }

    return -1;
  }

  private scrollCurrentQueueItemToTop(): void {
    setTimeout(() => {
      const container = this.queueContainer?.nativeElement;
      const card = this.queueCards?.get(this.currentIndex())?.nativeElement;

      if (!container || !card) {
        return;
      }

      container.scrollTo({
        top: card.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    });
  }
}
